/**
 * VÉRAULT Member Suite — OutstandingItemsBand (Wave 27.1 · Phase 4a).
 * ────────────────────────────────────────────────────────────────────
 * Smart "what still needs your attention" surface. Calm, never nagging.
 *
 *   Shows max 4 items; gold-tinted for high severity, neutral for the rest.
 *   Click → deep-link into the relevant section (Balances, Agreements, …).
 *   Silent when there's nothing outstanding (returns null).
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CircleDollarSign, FileSignature, FileText, Upload, MapPin, ChevronRight } from "lucide-react";

const ICONS = {
  payment: CircleDollarSign,
  signature: FileSignature,
  form: FileText,
  upload: Upload,
  approval: MapPin,
};

export default function OutstandingItemsBand({ items = [], compact = false }) {
  if (!items || items.length === 0) return null;
  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
      className="rounded-lg border border-[hsl(var(--accent-gold)/0.32)] bg-[hsl(var(--accent-gold)/0.06)] p-4 sm:p-5"
      data-testid="outstanding-items-band"
    >
      <div className="flex items-center justify-between mb-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">
          {compact ? "Still needed" : "A few quiet things still need you"}
        </p>
        <span className="font-mono text-[10px] text-[hsl(var(--foreground-muted))]" data-testid="outstanding-count">
          {items.length}
        </span>
      </div>
      <ul className="divide-y divide-[hsl(var(--accent-gold)/0.18)]" data-testid="outstanding-items-list">
        {items.slice(0, 4).map((it) => {
          const Icon = ICONS[it.kind] || FileText;
          const isHigh = it.severity === "high";
          return (
            <li key={it.id} className="py-2.5 first:pt-0 last:pb-0">
              <Link
                to={it.deep_link}
                className="flex items-start gap-3 group"
                data-testid={`outstanding-item-${it.id}`}
              >
                <span className={`mt-0.5 inline-flex items-center justify-center size-7 rounded-full flex-shrink-0 ${
                  isHigh
                    ? "bg-[hsl(var(--accent-gold)/0.18)] text-[hsl(var(--accent-gold))]"
                    : "bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground-muted))]"
                }`}>
                  <Icon className="size-3.5" strokeWidth={1.5} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-serif text-[15px] leading-tight text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--accent-gold))] transition-colors">
                    {it.label}
                  </span>
                  {it.detail && (
                    <span className="block font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] mt-0.5">
                      {it.detail}
                    </span>
                  )}
                </span>
                <ChevronRight className="size-4 text-[hsl(var(--foreground-muted))] group-hover:text-[hsl(var(--accent-gold))] mt-2 flex-shrink-0 transition-colors" strokeWidth={1.5} />
              </Link>
            </li>
          );
        })}
      </ul>
    </motion.section>
  );
}
